import { useEffect, useRef } from 'react'
import * as d3 from 'd3'
import { sankey, sankeyLinkHorizontal, SankeyNode, SankeyLink } from 'd3-sankey'
import { Card, Tag, Title, Text } from '@nimbus-ds/components'

interface FunnelStep {
  step_id: string
  step_name: string
  overall: {
    count: number
    percentage: number
  }
  control: {
    count: number
    percentage: number
  }
  treatment: {
    count: number
    percentage: number
  }
}

interface FunnelSankeyChartProps {
  funnelData: FunnelStep[]
  variant: 'control' | 'treatment' | 'both'
}

interface SankeyNodeExtended extends SankeyNode<object, object> {
  name: string
  variant?: string
  value?: number
}

interface SankeyLinkExtended extends SankeyLink<object, object> {
  source: SankeyNodeExtended
  target: SankeyNodeExtended
  value: number
  variant?: string
}

export function FunnelSankeyChart({ funnelData, variant = 'both' }: FunnelSankeyChartProps) {
  const svgRef = useRef<SVGSVGElement>(null)

  useEffect(() => {
    if (!svgRef.current || !funnelData || funnelData.length === 0) return

    // Clear previous chart
    d3.select(svgRef.current).selectAll('*').remove()

    const width = 900
    const height = Math.max(400, funnelData.length * 80)
    const margin = { top: 20, right: 200, bottom: 20, left: 200 }

    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('class', 'w-full h-auto')

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)

    const sankeyGenerator = sankey<SankeyNodeExtended, SankeyLinkExtended>()
      .nodeWidth(15)
      .nodePadding(30)
      .extent([[0, 0], [width - margin.left - margin.right, height - margin.top - margin.bottom]])

    // Build nodes and links based on variant selection
    const nodes: SankeyNodeExtended[] = []
    const links: SankeyLinkExtended[] = []

    if (variant === 'both') {
      // Create nodes for both variants side by side
      funnelData.forEach((step, i) => {
        nodes.push({
          name: `${step.step_name} (C)`,
          variant: 'control',
          value: step.control.count,
        })
        nodes.push({
          name: `${step.step_name} (T)`,
          variant: 'treatment',
          value: step.treatment.count,
        })

        // Create links between consecutive steps
        if (i < funnelData.length - 1) {
          const nextStep = funnelData[i + 1]

          // Control variant link
          links.push({
            source: nodes.length - 2,
            target: nodes.length,
            value: nextStep.control.count,
            variant: 'control',
          } as unknown as SankeyLinkExtended)

          // Treatment variant link
          links.push({
            source: nodes.length - 1,
            target: nodes.length + 1,
            value: nextStep.treatment.count,
            variant: 'treatment',
          } as unknown as SankeyLinkExtended)
        }
      })
    } else {
      // Single variant view
      const variantData = variant === 'control' ? 'control' : 'treatment'
      funnelData.forEach((step, i) => {
        nodes.push({
          name: step.step_name,
          variant: variantData,
          value: step[variantData].count,
        })

        if (i < funnelData.length - 1) {
          const nextStep = funnelData[i + 1]
          links.push({
            source: i,
            target: i + 1,
            value: nextStep[variantData].count,
            variant: variantData,
          } as unknown as SankeyLinkExtended)
        }
      })
    }

    const sankeyData = sankeyGenerator({
      nodes: nodes as any,
      links: links as any,
    })

    // Color schemes
    const controlColor = '#3b82f6' // blue-500
    const treatmentColor = '#10b981' // green-500
    const controlLightColor = '#93c5fd' // blue-300
    const treatmentLightColor = '#6ee7b7' // green-300

    // Draw links
    g.append('g')
      .attr('fill', 'none')
      .selectAll('path')
      .data(sankeyData.links)
      .join('path')
      .attr('d', sankeyLinkHorizontal() as any)
      .attr('stroke', (d: any) =>
        d.variant === 'control' ? controlLightColor : treatmentLightColor
      )
      .attr('stroke-width', (d: any) => Math.max(1, d.width || 0))
      .attr('opacity', 0.5)
      .on('mouseover', function() {
        d3.select(this).attr('opacity', 0.8)
      })
      .on('mouseout', function() {
        d3.select(this).attr('opacity', 0.5)
      })
      .append('title')
      .text((d: any) => {
        const dropoff = d.source.value ? ((d.source.value - d.value) / d.source.value * 100) : 0
        return `${d.source.name} → ${d.target.name}\nSesiones: ${d.value.toLocaleString()}\nDrop-off: ${dropoff.toFixed(1)}%`
      })

    // Draw nodes
    const node = g.append('g')
      .selectAll('rect')
      .data(sankeyData.nodes)
      .join('g')

    node.append('rect')
      .attr('x', (d: any) => d.x0)
      .attr('y', (d: any) => d.y0)
      .attr('height', (d: any) => Math.max(1, d.y1 - d.y0))
      .attr('width', (d: any) => d.x1 - d.x0)
      .attr('fill', (d: any) =>
        d.variant === 'control' ? controlColor : treatmentColor
      )
      .attr('stroke', '#000')
      .attr('stroke-width', 0.5)
      .attr('rx', 2)
      .append('title')
      .text((d: any) => `${d.name}\n${d.value?.toLocaleString() || 0} sesiones`)

    // Add node labels
    node.append('text')
      .attr('x', (d: any) => (d.x0 + d.x1) / 2)
      .attr('y', (d: any) => (d.y0 + d.y1) / 2)
      .attr('dy', '0.35em')
      .attr('text-anchor', 'middle')
      .attr('fill', 'white')
      .attr('font-size', '10px')
      .attr('font-weight', 'bold')
      .style('pointer-events', 'none')
      .text((d: any) => d.value?.toLocaleString() || '0')

    // Add step names on the left
    node.append('text')
      .attr('x', (d: any) => d.x0 - 10)
      .attr('y', (d: any) => (d.y0 + d.y1) / 2)
      .attr('dy', '0.35em')
      .attr('text-anchor', 'end')
      .attr('font-size', '12px')
      .attr('fill', 'currentColor')
      .style('pointer-events', 'none')
      .text((d: any) => d.name)

  }, [funnelData, variant])

  if (!funnelData || funnelData.length === 0) {
    return (
      <Card>
        <Card.Header>
          <Title as="h3">Visualización del Funnel</Title>
          <Text>Flujo de sesiones a través de cada paso del funnel</Text>
        </Card.Header>
        <Card.Body>
          <div className="flex items-center justify-center h-64">
            <Text>No hay datos de funnel disponibles</Text>
          </div>
        </Card.Body>
      </Card>
    )
  }

  const totalDropoff = funnelData.length > 0
    ? ((funnelData[0].overall.count - funnelData[funnelData.length - 1].overall.count) / funnelData[0].overall.count * 100)
    : 0

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center justify-between">
          <div>
            <Title as="h3">Visualización del Funnel - Diagrama de Sankey</Title>
            <Text>
              Flujo de sesiones a través de cada paso del funnel
            </Text>
          </div>
          <div className="flex gap-2">
            <div className="shrink-0 whitespace-nowrap">
              <Tag appearance="neutral">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                Control
              </Tag>
            </div>
            <div className="shrink-0 whitespace-nowrap">
              <Tag appearance="neutral">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                Tratamiento
              </Tag>
            </div>
          </div>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="space-y-6">
          {/* Overall metrics */}
          <div className="grid grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg">
            <div>
              <Text fontSize="caption">Sesiones Iniciales</Text>
              <Text fontSize="highlight" fontWeight="bold" color="neutral-textHigh">{funnelData[0]?.overall.count.toLocaleString() || 0}</Text>
            </div>
            <div>
              <Text fontSize="caption">Conversiones Finales</Text>
              <Text fontSize="highlight" fontWeight="bold" color="neutral-textHigh">
                {funnelData[funnelData.length - 1]?.overall.count.toLocaleString() || 0}
              </Text>
            </div>
            <div>
              <Text fontSize="caption">Drop-off Total</Text>
              <p className="text-2xl font-bold text-amber-600">{totalDropoff.toFixed(1)}%</p>
            </div>
          </div>

          {/* Sankey diagram */}
          <div className="overflow-x-auto">
            <svg ref={svgRef}></svg>
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}
